import { Pencil, RotateCcw, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import Gravatar from 'react-gravatar'
import {
  useGetOrgUsersLimits,
  useUpdateOrgUserLimit,
  useResetOrgUserLimit,
} from '@/services/settings'
import { useGetOrgUsers } from '@/services/user-management'
import { toast } from 'react-hot-toast'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { ConfirmationDialog } from '@/components/confirmation-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { DataTable, TableColumn } from '@/components/data-table/DataTable'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ResetOrgUserLimitFields } from '@/services/inference.gen'

type UserStorageVolumeCardProps = {
  orgStorageLimit: number
  orgStorageCountLimit: number
}

type UserLimitItem = {
  user_id: number
  email: string
  first_name?: string
  last_name?: string
  avatar_url?: string
  org_per_user_storage_gb: number
  org_per_user_storage_count: number
}

type DialogState = 'none' | 'edit' | 'confirm' | 'reset' | 'add' | 'add-confirm'

// Form schema
const editLimitFormSchema = z.object({
  storageLimit: z.number().min(1, 'Storage limit must be at least 1'),
  storageCountLimit: z
    .number()
    .min(1, 'Storage count limit must be at least 1'),
})

const addLimitFormSchema = z.object({
  userId: z.string().min(1, 'Please select a user'),
  storageLimit: z.number().min(1, 'Storage limit must be at least 1'),
  storageCountLimit: z
    .number()
    .min(1, 'Storage count limit must be at least 1'),
})

type EditLimitFormValues = z.infer<typeof editLimitFormSchema>
type AddLimitFormValues = z.infer<typeof addLimitFormSchema>

export const UserStorageVolumeCard = ({
  orgStorageLimit,
  orgStorageCountLimit,
}: UserStorageVolumeCardProps) => {
  const [page, setPage] = useState(0)

  // Fetch storage limits
  const {
    data,
    isLoading,
    isFetching,
    refetch: refetchOrgUsersLimits,
  } = useGetOrgUsersLimits(page)

  // Fetch all org users for the dropdown
  const { data: orgUsersData } = useGetOrgUsers('active', '', undefined)

  // User for row operations
  const [selectedUser, setSelectedUser] = useState<{
    id: number
    email: string
    storageLimit: number
    storageCountLimit: number
  } | null>(null)

  const [dialogState, setDialogState] = useState<DialogState>('none')

  const form = useForm<EditLimitFormValues>({
    resolver: zodResolver(editLimitFormSchema),
    defaultValues: {
      storageLimit: 0,
      storageCountLimit: 0,
    },
  })

  const addLimitForm = useForm<AddLimitFormValues>({
    resolver: zodResolver(addLimitFormSchema),
    defaultValues: {
      userId: '',
      storageLimit: orgStorageLimit,
      storageCountLimit: orgStorageCountLimit,
    },
  })

  const mutation = useUpdateOrgUserLimit()
  const resetMutation = useResetOrgUserLimit()

  const closeDialog = () => {
    setDialogState('none')
    setSelectedUser(null)
  }

  const onDialogChange = (open: boolean) => {
    if (!open) closeDialog()
  }

  const handleEditClick = (user: {
    id: number
    email: string
    storageLimit: number
    storageCountLimit: number
  }) => {
    setSelectedUser(user)
    form.setValue('storageLimit', user.storageLimit)
    form.setValue('storageCountLimit', user.storageCountLimit)
    setDialogState('edit')
  }

  const handleEditSubmit = (values: EditLimitFormValues) => {
    if (!selectedUser) return

    setSelectedUser({
      ...selectedUser,
      storageLimit: values.storageLimit,
      storageCountLimit: values.storageCountLimit,
    })
    setDialogState('confirm')
  }

  const handleResetClick = (user: {
    id: number
    email: string
    storageLimit: number
    storageCountLimit: number
  }) => {
    setSelectedUser(user)
    setDialogState('reset')
  }

  const handleAddClick = () => {
    addLimitForm.reset({
      userId: '',
      storageLimit: orgStorageLimit,
      storageCountLimit: orgStorageCountLimit,
    })
    setDialogState('add')
  }

  const handleAddSubmit = (values: AddLimitFormValues) => {
    const userId = parseInt(values.userId)
    const selectedUserOrg = orgUsersData?.allUsers.find(
      (userOrg) => userOrg.user_id === userId
    )

    if (!selectedUserOrg || !selectedUserOrg.user?.email) {
      toast.error('User information not found')
      setDialogState('none')
      return
    }

    setSelectedUser({
      id: userId,
      email: selectedUserOrg.user.email,
      storageLimit: values.storageLimit,
      storageCountLimit: values.storageCountLimit,
    })

    setDialogState('add-confirm')
  }

  const handleConfirmUpdate = () => {
    if (!selectedUser) return

    mutation.mutate(
      {
        org_per_user_storage_gb: selectedUser.storageLimit,
        org_per_user_storage_count: selectedUser.storageCountLimit,
        user_id: selectedUser.id,
      },
      {
        onSuccess: () => {
          closeDialog()
          refetchOrgUsersLimits()
        },
        onError: (err) => {
          console.error('Failed to update storage limits:', err)
          toast.error(JSON.stringify(err))
          closeDialog()
        },
      }
    )
  }

  const handleConfirmReset = () => {
    if (!selectedUser) return

    resetMutation.mutate(
      {
        user_id: selectedUser.id,
        fields: ResetOrgUserLimitFields.OrgPerUserStorage,
      },
      {
        onSuccess: () => {
          closeDialog()
          refetchOrgUsersLimits()
        },
        onError: (err) => {
          console.error('Failed to reset storage limits:', err)
          toast.error(JSON.stringify(err))
          closeDialog()
        },
      }
    )
  }

  // Transform the API response to a more usable format, using user management data for accurate user info
  const userLimits: UserLimitItem[] = (data?.limits || [])
    .map((item) => {
      // Find the corresponding user from orgUsersData for accurate user info including avatar
      const userFromOrgData = orgUsersData?.allUsers.find(
        (user) => user.user_id === item.user?.user_id
      )

      return {
        user_id: item.user?.user_id || 0,
        email: item.user?.email || '',
        first_name:
          userFromOrgData?.profile_metadata?.first_name ||
          item.user_organization?.profile_metadata?.first_name,
        last_name:
          userFromOrgData?.profile_metadata?.last_name ||
          item.user_organization?.profile_metadata?.last_name,
        avatar_url:
          userFromOrgData?.profile_metadata?.avatar_preview_url ||
          item.user_organization?.profile_metadata?.avatar_preview_url,
        org_per_user_storage_gb: item.user_limit.org_per_user_storage_gb,
        org_per_user_storage_count: item.user_limit.org_per_user_storage_count,
      }
    })
    .filter(
      (item) =>
        !isNaN(item.org_per_user_storage_gb) &&
        item.org_per_user_storage_gb !== 0 &&
        !isNaN(item.org_per_user_storage_count) &&
        item.org_per_user_storage_count !== 0
    )

  // Define columns for the DataTable
  const columns: TableColumn<UserLimitItem>[] = [
    {
      id: 'user',
      header: 'User',
      width: 'w-1/3',
      cell: (item) => {
        return (
          <div className="flex items-center gap-3">
            {item.avatar_url ? (
              <img
                src={item.avatar_url}
                alt={`${item.first_name || ''} ${item.last_name || ''}'s avatar`}
                className="h-10 w-10 rounded-full object-cover border border-border"
              />
            ) : (
              <Gravatar
                email={item.email || ''}
                className="h-10 w-10 rounded-full border border-border"
              />
            )}
            <div>
              <p className="font-medium">
                {item.first_name && item.last_name
                  ? `${item.first_name} ${item.last_name}`
                  : item.email || '--'}
              </p>
              <p className="text-sm text-foreground/70">{item.email}</p>
            </div>
          </div>
        )
      },
    },
    {
      id: 'storageLimit',
      header: 'Storage Limit',
      width: 'w-1/4',
      cell: (item) => (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-primary"></span>
          <span className="font-medium">{item.org_per_user_storage_gb} GB</span>
        </div>
      ),
    },
    {
      id: 'volumeLimit',
      header: 'Volume Limit',
      width: 'w-1/4',
      cell: (item) => (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-blue-500"></span>
          <span className="font-medium">
            {item.org_per_user_storage_count} volumes
          </span>
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      width: 'w-1/6',
      cell: (item) => {
        return (
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                handleEditClick({
                  id: item.user_id,
                  email: item.email,
                  storageLimit: item.org_per_user_storage_gb,
                  storageCountLimit: item.org_per_user_storage_count,
                })
              }
              className="text-xs"
            >
              <Pencil className="mr-1 h-3.5 w-3.5" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                handleResetClick({
                  id: item.user_id,
                  email: item.email,
                  storageLimit: item.org_per_user_storage_gb,
                  storageCountLimit: item.org_per_user_storage_count,
                })
              }
              className="text-xs text-destructive hover:text-destructive"
            >
              <RotateCcw className="mr-1 h-3.5 w-3.5" />
              Reset
            </Button>
          </div>
        )
      },
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex justify-end w-full items-center mb-4">
        <Button
          variant="outline"
          onClick={handleAddClick}
          size="sm"
          data-testid="add-user-storage-override-button"
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Add User Override
        </Button>
      </div>

      <DataTable
        data={userLimits}
        columns={columns}
        keyExtractor={(item) => item.user_id}
        isLoading={isLoading}
        isFetchingNextPage={isFetching && page > 0}
        hasNextPage={data?.has_more || false}
        fetchNextPage={() => setPage((p) => p + 1)}
        emptyStateMessage="No users with custom limits found"
      />

      <Dialog open={dialogState === 'edit'} onOpenChange={onDialogChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User Storage Limits</DialogTitle>
            <DialogDescription>
              Set custom storage limits for this user.
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleEditSubmit)}
              className="space-y-4 py-4"
            >
              <FormField
                control={form.control}
                name="storageLimit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Storage Limit (GB)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value === 0 ? '' : field.value}
                        onChange={(e) => {
                          const value =
                            e.target.value === '' ? 0 : Number(e.target.value)
                          field.onChange(value)
                        }}
                        placeholder="Enter storage limit in GB"
                        className="w-full"
                        data-testid="storage-gb-limit-input"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="storageCountLimit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Storage Volumes Limit</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value === 0 ? '' : field.value}
                        onChange={(e) => {
                          const value =
                            e.target.value === '' ? 0 : Number(e.target.value)
                          field.onChange(value)
                        }}
                        placeholder="Enter max number of volumes"
                        className="w-full"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className="mt-4">
                <Button variant="outline" type="button" onClick={closeDialog}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    !form.formState.isValid ||
                    form.getValues('storageLimit') < 1 ||
                    form.getValues('storageCountLimit') < 1
                  }
                >
                  Continue
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Add User Limit Dialog */}
      <Dialog open={dialogState === 'add'} onOpenChange={onDialogChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add User Storage Limits</DialogTitle>
            <DialogDescription>
              Set custom storage limits for a user.
            </DialogDescription>
          </DialogHeader>

          <Form {...addLimitForm}>
            <form
              onSubmit={addLimitForm.handleSubmit(handleAddSubmit)}
              className="space-y-4 py-4"
            >
              <FormField
                control={addLimitForm.control}
                name="userId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Select User</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="user-select-trigger">
                          <SelectValue placeholder="Select a user" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {orgUsersData?.allUsers?.map((userOrg) => {
                          // Check if this user already has a custom limit by ID
                          const hasCustomLimit = userLimits.some(
                            (item) => item.user_id === userOrg.user_id
                          )

                          return (
                            <SelectItem
                              key={userOrg.user_id}
                              value={userOrg.user_id.toString()}
                              disabled={hasCustomLimit}
                              data-testid={`user-option-${userOrg.user?.email}`}
                            >
                              {userOrg.user?.email || ''}
                              {hasCustomLimit && ' (already has custom limit)'}
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={addLimitForm.control}
                name="storageLimit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Storage Limit (GB)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value === 0 ? '' : field.value}
                        onChange={(e) => {
                          const value =
                            e.target.value === '' ? 0 : Number(e.target.value)
                          field.onChange(value)
                        }}
                        placeholder="Enter storage limit in GB"
                        className="w-full"
                        data-testid="storage-gb-limit-input"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={addLimitForm.control}
                name="storageCountLimit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Storage Volumes Limit</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value === 0 ? '' : field.value}
                        onChange={(e) => {
                          const value =
                            e.target.value === '' ? 0 : Number(e.target.value)
                          field.onChange(value)
                        }}
                        placeholder="Enter max number of volumes"
                        className="w-full"
                        data-testid="storage-volumes-limit-input"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className="mt-4">
                <Button
                  variant="outline"
                  type="button"
                  onClick={closeDialog}
                  data-testid="cancel-user-limit-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    !addLimitForm.formState.isValid ||
                    addLimitForm.getValues('userId') === '' ||
                    addLimitForm.getValues('storageLimit') < 1 ||
                    addLimitForm.getValues('storageCountLimit') < 1
                  }
                  data-testid="continue-user-limit-button"
                >
                  Continue
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Confirm Update Dialog */}
      <ConfirmationDialog
        open={dialogState === 'confirm' || dialogState === 'add-confirm'}
        onOpenChange={onDialogChange}
        title="Confirm User Storage Limits Override"
        description={
          selectedUser
            ? `You're about to set ${selectedUser.storageLimit} GB storage limit and ${selectedUser.storageCountLimit} storage volumes limit for ${selectedUser.email}.`
            : ''
        }
        onConfirm={handleConfirmUpdate}
        onCancel={closeDialog}
        buttons={{
          confirm: {
            label: mutation.isPending ? 'Applying...' : 'Apply Override',
            isLoading: mutation.isPending,
          },
          cancel: { label: 'Cancel' },
        }}
        confirmation={{
          text: `${selectedUser?.storageLimit},${selectedUser?.storageCountLimit}`,
          prompt: `Please type "${selectedUser?.storageLimit},${selectedUser?.storageCountLimit}" to confirm:`,
        }}
        info={{
          warningPoints: [
            "This user's storage limits will be set independently from the organization-wide limits",
            'These limits will apply to future resources. Existing resources will continue running based on the limits from when they were created.',
          ],
        }}
      />

      {/* Confirm Reset Dialog */}
      <ConfirmationDialog
        open={dialogState === 'reset'}
        onOpenChange={onDialogChange}
        title="Reset User Storage Limits to Default"
        description={
          selectedUser
            ? `You're about to reset ${selectedUser.email}'s storage limits to the organization default.`
            : ''
        }
        onConfirm={handleConfirmReset}
        onCancel={closeDialog}
        buttons={{
          confirm: {
            label: resetMutation.isPending
              ? 'Resetting...'
              : 'Reset to Default',
            isLoading: resetMutation.isPending,
            isDestructive: true,
          },
          cancel: { label: 'Cancel' },
        }}
        info={{
          alert: {
            title: 'Warning',
            description:
              'This will remove any custom storage limits for this user. They will follow the organization-wide default limits.',
          },
        }}
      />
    </div>
  )
}
